import {
  DesignerColor,
  newDesignerColor,
  DesignerGradientDirection,
  LabelAttributes,
} from '@decisions/server/Decisions/Silverlight/UI/Utilities';
import { DesignerGradient } from '@decisions/server/Decisions/Silverlight/UI/Utilities/DesignerGradient';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { ThemeGradientType } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Theme/ThemeGradientType';
import { color as d3Color } from 'd3-color';
import { CSSProperties } from 'react';

export function getColorCssString(value: DesignerColor | string): string {
  if (typeof value === 'string') {
    return value;
  } else if (value.Type === ThemeGradientType.LinearGradient && Array.isArray(value.GradientDefinition)) {
    const colorStops = value.GradientDefinition.sort((a, b) => a.Offset - b.Offset)
      .map(getGradientColorString)
      .join(', ');
    const dir = getCssGradientDirection(value);
    return `linear-gradient(${dir}, ${colorStops})`;
  }
  return value.ColorName || '';
}

function getGradientColorString(gradient: DesignerGradient): string {
  const start = gradient.Color;
  const startOffset = convertOffset(gradient.Offset);
  return `${start} ${startOffset}`;
}

function convertOffset(offset: number) {
  return offset * 100 + '%';
}

export function getDefaultDesignerColor(): DesignerColor {
  return newDesignerColor({
    ColorName: 'Transparent',
    GradientDirection: 0,
    Opacity: 0,
    Type: 0,
  });
}

function getColorValue(child: ContextProviderNode): DesignerColor {
  return child.Value && child.Value.OutputValue ? child.Value.OutputValue : getDefaultDesignerColor();
}

export function getColorEditorString(child: ContextProviderNode): string {
  const value = getColorValue(child);
  return getColorCssString(value);
}

/**
 * Convert an RGB CSS string value to a hexadecimal one.
 *
 * lifted from platform/hosts/Decisions.Web.MVC.Host/Decisions.Web.MVC.Host/Scripts/ColorEditor/ColorConverterHelpers.ts
 * TODO needs deduping after we are generating types from FrontEnd for MVC.
 *
 * @param rgb rgb value to convert to HEX
 * @returns hexadecimal color notation
 */
export function rgbToHex(rgb: string | null) {
  if (!rgb) {
    return '';
  }
  const rgbMatch = rgb.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
  return rgbMatch?.length === 4
    ? '#' +
        ('0' + parseInt(rgbMatch[1], 10).toString(16)).slice(-2) +
        ('0' + parseInt(rgbMatch[2], 10).toString(16)).slice(-2) +
        ('0' + parseInt(rgbMatch[3], 10).toString(16)).slice(-2)
    : '';
}

/**
 * Convert an hexadecimal CSS string value to an RBG one.
 *
 * lifted from platform/hosts/Decisions.Web.MVC.Host/Decisions.Web.MVC.Host/Scripts/ColorEditor/ColorConverterHelpers.ts
 * TODO needs deduping after we are generating types from FrontEnd for MVC.
 *
 * @param hex string to convert to rgb(a)
 * @param includeAlpha include alpha channel. true by default
 * @returns rgb(a) color notation
 */
export function hexToRgb(hex: string, includeAlpha: boolean = true) {
  hex = hex.replace('#', '');
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  if (hex.length === 8 && includeAlpha) {
    const a = parseInt(hex.substring(6, 8), 16);
    if (a) {
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    }
  }
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Shift the hue of a hex or RGBA color.
 *
 * // TODO - to do a true hue shift, without affecting color or lightness, this should convert to HSL and back.
 * // or maybe do the math on how much to decrease the other colors based on the total
 * // OR d3.interpolate has some color functions we could make use of for this kind of thing, too
 *
 * @param colorString to shift
 * @param shift primary color to update (rgb)
 * @param amount 0 - 255. default is 100
 * @returns adjusted rgb(a) color
 */
export function shiftHue(colorString: string, shift: 'r' | 'g' | 'b', amount = 100) {
  const wasHex = colorString[0] === '#';
  if (wasHex) {
    colorString = hexToRgb(colorString);
  }
  const increase = Math.round(amount);
  const decrease = Math.round(amount * 0.5);
  const split = colorString.replace(/(?:rgba?\()|\)/, '').split(',');
  const shiftIndex = shift === 'r' ? 0 : shift === 'g' ? 1 : 2;
  const numbers = split
    .map((val, i) => (i < 3 ? parseInt(val, 10) : parseFloat(val)))
    .map((val, i) => (i === 3 ? val : i === shiftIndex ? Math.min(val + increase, 255) : Math.max(val - decrease, 0)));
  // numbers[shiftIndex] = Math.min(numbers[shiftIndex] + amount, 255);

  const prefix = split.length > 3 ? 'rgba' : 'rgb';
  // should we put it back to hex if it was hex?
  return `${prefix}(${numbers.join(', ')})`;
}

export function darken(colorString: string, amount?: number): string {
  // taking advantage of d3 here, since it is a dep of nivo, also, but should we need to move away from d3,
  // this wrapper should reduce code-changes elsewhere.
  return (
    d3Color(colorString)
      ?.darker(amount)
      .formatRgb() ?? colorString
  );
}

/**
 * This returns a direction string, e.g. "to top right".
 *
 * TODO If we wanted these to more accurately reflect the cardinal indicators in
 * the dialog, we should consider converting to `deg` units. Word-based direction is subject
 * to the dimensions of the element, which is sometimes what users want, but sometimes not.
 * Leaving as is for now, to ensure consistent behavior.
 * Ideally, we could let them specify any direction on a radial slider, not just cardinals.
 *
 * @param color to process
 * @returns linear-gradient direction css string
 */
export function getCssGradientDirection(color: DesignerColor): string {
  const direction = color.GradientDirection;
  switch (direction) {
    case DesignerGradientDirection.North:
      return 'to top';
    case DesignerGradientDirection.NorthEast:
      return 'to top right';
    case DesignerGradientDirection.East:
      return 'to right';
    case DesignerGradientDirection.SouthEast:
      return 'to bottom right';
    case DesignerGradientDirection.SouthWest:
      return 'to bottom left';
    case DesignerGradientDirection.West:
      return 'to left';
    case DesignerGradientDirection.NorthWest:
      return 'to top left';
    case DesignerGradientDirection.South:
    default:
      return '';
  }
}

export function getComponentStyle(labelAttribute: LabelAttributes | undefined): CSSProperties | undefined {
  if (labelAttribute && labelAttribute.LabelColor) {
    if (labelAttribute.LabelColor.Type === ThemeGradientType.SolidColor) {
      const { FontFamily: fontFamily, FontSize: fontSize, LabelColor, IsBold, IsItalic, IsUnderlined } = labelAttribute;
      return {
        fontFamily,
        fontSize,
        color: getRGBAColor(LabelColor) || undefined,
        fontStyle: IsItalic ? 'italic' : 'normal',
        fontWeight: IsBold ? 'bold' : 'normal',
        textDecoration: IsUnderlined ? 'underline' : 'none',
      };
    }
  }
  return undefined;
}

export function getRGBAColor(designerColor?: DesignerColor) {
  if (designerColor == null) return null;

  // let opacity = designerColor.Opacity.toFixed(2);
  let red, green, blue, opacity;
  if (designerColor && designerColor.ColorName) {
    if (designerColor.ColorName.indexOf('#') === 0) {
      let withoutHash = designerColor.ColorName.replace('#', '');
      if (withoutHash.length === 8) {
        //colorName contains values for opacity
        //we are using aRGB and not RGBa.
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(designerColor.ColorName);
        if (result) {
          opacity = parseInt(result[1], 16);
          red = parseInt(result[2], 16);
          green = parseInt(result[3], 16);
          blue = parseInt(result[4], 16);
        }
      } else if (withoutHash.length === 6) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(designerColor.ColorName);
        if (result) {
          red = parseInt(result[1], 16);
          green = parseInt(result[2], 16);
          blue = parseInt(result[3], 16);
        }
        if (designerColor.Opacity) opacity = designerColor.Opacity.toString();
      }
    } // colorname may contains rgb/rgba
    else {
      var regex = /^rgb\(([^)]+)\)/g;
      var matches = regex.exec(designerColor.ColorName);
      if (matches && matches.length > 1) {
        let values = matches[1].split(',');
        red = values[0];
        green = values[1];
        blue = values[2];

        if (values.length > 3) opacity = values[3];
      } else {
        return designerColor.ColorName;
      }
    }
  }
  if (!opacity) opacity = designerColor.Opacity.toString();
  if (!opacity) return `rgb(${red},${green},${blue})`;

  return `rgba(${red},${green},${blue},${opacity})`;
}
